import { Game, Participant, PlayerStanding, gameResult, rankPlayers, sharedPositions } from "../types";

/**
 * Illan tulos osallistujittain: sijoitus, läsnäolo ja se mitä kantaan
 * pitää kirjoittaa, jotta osallistujarivi vastaa otteluita.
 *
 * Yksi lasku kahdelle napille. Illan päättäminen (`closeEvent`) ja
 * päätetyn illan uudelleenlaskenta käyttävät tätä molemmat, joten ne eivät
 * voi erota toisistaan. Aiemmin uudelleenlaskenta oli vain kehittäjän
 * skriptissä (`functions/scripts/liiga-diag.mjs --korjaa`), eli käytännössä
 * sitä ei tehty: jälkikäteen korjattu tulos jätti liigapisteet vanhoiksi.
 *
 * Läsnäolo = ylläpitäjä merkitsi paikalle TAI pelasi vähintään yhden
 * ratkenneen ottelun. Aiemmin läsnäolo tuli `confirmed`-kentästä, joka
 * tarkoittaa ilmoittautunutta eikä paikalla ollutta — tulematta jäänyt sai
 * liigan läsnäolopisteen. Kerran kirjattua läsnäoloa ei pyyhitä: vanhat,
 * ennen tätä päätetyt illat säilyttävät pisteensä uudelleenlaskennassa.
 */
export interface NightRow {
  participant: Participant;
  standing?: PlayerStanding;
  played: boolean;
  position: number | null;
  attended: boolean;
  /** Muutokset kantaan. `null` = kenttä poistetaan. Tyhjä = ei muutosta. */
  patch: { attended?: boolean; finalPosition?: number | null };
}

export function nightResults(games: Game[], parts: Participant[]): NightRow[] {
  const standings = rankPlayers(games, parts.map(p => p.displayName));
  const positions = sharedPositions(standings);
  const index = new Map<string, number>();
  standings.forEach((s, i) => index.set(s.name.toLowerCase(), i));

  return parts.map(p => {
    const i = index.get(p.displayName.toLowerCase());
    const standing = i != null ? standings[i] : undefined;
    const played = (standing?.played ?? 0) > 0;
    const position = played && i != null ? positions[i] : null;
    const attended = p.attended === true || played;

    const patch: NightRow["patch"] = {};
    if (attended && p.attended !== true) patch.attended = true;
    if (position !== null && p.finalPosition !== position) patch.finalPosition = position;
    // Vain pelaamattomia pelejä (0–0) pelannut ei saa sijoitusta.
    if (position === null && p.finalPosition != null) patch.finalPosition = null;

    return { participant: p, standing, played, position, attended, patch };
  });
}

/**
 * Ovatko kannassa olevat sijoitukset jäljessä otteluista.
 *
 * Vain `finalPosition` ratkaisee. Pelanneelta puuttuva `attended` ei
 * muuta liigapisteitä (sijoitus tuo läsnäolopisteen mukanaan), joten siitä
 * ei kannata hälyttää — uudelleenlaskenta kirjaa sen silti samalla.
 */
export function nightIsStale(games: Game[], parts: Participant[]): boolean {
  return nightResults(games, parts).some(r => "finalPosition" in r.patch);
}

/**
 * Illan päättämisen vahvistusteksti.
 *
 * Päättäminen kirjaa tilastot ja liigasijat siitä mitä kannassa on sillä
 * hetkellä. Ottelu jonka tulos jäi kirjaamatta (tai on 0–0) lasketaan
 * pelaamattomaksi. Aiemmin vahvistus oli sama riippumatta siitä, oliko
 * illasta kirjattu kaikki vai ei mitään — ensimmäisessä liigaillassa
 * kierros 1 jäi 0–0:aan ja ilta päätettiin siitä huolimatta.
 *
 * Tasapeli on oikea tulos (aika loppui, docs/liiga.md), joten siitä ei
 * varoiteta.
 *
 * Liigaillassa kerrotaan lisäksi ilmoittautuneet, jotka eivät pelanneet
 * eivätkä ole merkitty paikalle: he jäävät ilman läsnäolopistettä.
 */
export function closeEventConfirmText(
  games: Game[], lang: "fi" | "en", isLeague: boolean, parts: Participant[] = [],
): string {
  const fi = lang !== "en";
  const lines: string[] = [];

  const missing = games.filter(g => gameResult(g) === "unplayed");
  const counted = fi
    ? (isLeague ? "Niitä ei lasketa tilastoihin eikä liigataulukkoon." : "Niitä ei lasketa tilastoihin.")
    : (isLeague ? "They will not count in stats or the league table." : "They will not count in stats.");

  if (missing.length > 0 && missing.length === games.length) {
    lines.push(fi
      ? `⚠️ Yhdenkään ottelun tulosta ei ole kirjattu (${games.length} ottelua). ${counted}`
      : `⚠️ No match results have been entered (${games.length} matches). ${counted}`);
  } else if (missing.length > 0) {
    // Kierrokset joilta tulos puuttuu, jotta ylläpitäjä tietää mistä etsiä.
    // Käsin lisätyillä otteluilla ei ole kierrosta.
    const rounds = Array.from(new Set(missing.map(g => g.round).filter((r): r is number => r != null)))
      .sort((a, b) => a - b);
    const where: string[] = [];
    if (rounds.length > 0) where.push((fi ? (rounds.length > 1 ? "kierrokset " : "kierros ") : (rounds.length > 1 ? "rounds " : "round ")) + rounds.join(", "));
    if (missing.some(g => g.round == null)) where.push(fi ? "lisäottelut" : "added matches");
    const n = missing.length;
    const head = fi ? `⚠️ ${n} ottelulta puuttuu tulos` : `⚠️ ${n} ${n === 1 ? "match has" : "matches have"} no result`;
    lines.push(`${head}${where.length ? ` (${where.join("; ")})` : ""}. ${counted}`);
  }

  if (isLeague) {
    const absent = nightResults(games, parts)
      .filter(r => r.participant.confirmed && !r.attended)
      .map(r => r.participant.displayName);
    if (absent.length > 0) {
      lines.push(fi
        ? `Ei pelannut eikä merkitty paikalle: ${absent.join(", ")}. Läsnäolopistettä ei tule — merkitse paikalla olleet osallistujalistasta.`
        : `Did not play and not marked present: ${absent.join(", ")}. No attendance points — mark who came in the participant list.`);
    }
  }

  if (lines.length === 0) {
    return fi
      ? "Päätetäänkö tapahtuma? Tulokset kirjataan pelaajien tilastoihin."
      : "End this event? Results are recorded in player stats.";
  }
  return lines.join("\n\n") + "\n\n" + (fi ? "Päätetäänkö tapahtuma silti?" : "End the event anyway?");
}
